import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { FieldBaseComponent } from '../field-base/field-base.model';

export interface ITickmark {
    label?: string;
    value?: any;
    isDefault?: boolean;
    _rangeValue?: number;
}


export interface IMinMaxRanges {
    minValue: number;
    maxValue: number;
}

@Component({
    selector: 'app-input-range',
    template: `
        <div class="input-range" [formGroup]="parentForm" [ngClass]="{'is-disabled': isDisabled}">
            <label>
                <div class="input-range__header">
                    <div *ngIf="label">{{ label }}</div>
                    <div *ngIf="labelUnit" class="input-range__header-unit">{{ labelUnit }}</div>
                </div>
                <div class="input-range__input-wrapper">
                    <div
                        *ngIf="controlledTrackStart !== undefined && controlledTrackEnd !== undefined"
                        class="input-range__controlled-track-progress-bg"
                    ></div>
                    <div
                        *ngIf="controlledTrackStart !== undefined && controlledTrackEnd !== undefined"
                        class="input-range__controlled-track-progress"
                        [ngStyle]="{
                            left: controlledTrackStart + '%',
                            right: controlledTrackEnd + '%'
                        }"
                    ></div>
                    <input
                        type="range"
                        [formControlName]="controlName"
                        [ngClass]="cssClasses"
                        [ngStyle]="{
                            'background-size': maxVal
                                ? ((inputControl?.value - (minVal || 0)) / ((maxVal || 0) - (minVal || 0))) * 100 + '%, 100%'
                                : inputControl?.value + '%, 100%'
                        }"
                        (mouseup)="handleChange($event)"
                        (touchend)="handleChange($event)"
                        (keyup)="handleChange($event)"
                        (input)="handleLiveUpdates($event)"
                        [min]="minVal"
                        [max]="maxVal"
                        [step]="inputStepSize"
                    />
                </div>
            </label>
            <div
                *ngIf="!hideRangeMarks"
                class="input-range-marks text-secondary"
                [ngClass]="{ 'input-range-marks--only-min-max': minVal !== undefined && maxVal !== undefined }"
            >
                <ng-container *ngIf="!minVal && !maxVal">
                    <div
                        class="input-range-marks__mark"
                        *ngFor="let mark of marks"
                        (click)="handleMarkClick(mark)"
                        [ngStyle]="{ left: mark._rangeValue + '%' }"
                    >
                        {{ mark.label }}
                    </div>
                </ng-container>
                <div
                    class="input-range-marks__mark"
                    *ngIf="minVal !== undefined"
                    (click)="handleMarkClick({ value: minVal, _rangeValue: minVal })"
                    [ngStyle]="{ left: '0%' }"
                >
                    <ng-container *ngIf="minValLabel">{{ minValLabel }} </ng-container>
                    <ng-container *ngIf="!minValLabel">
                        {{ minVal }}
                    </ng-container>
                </div>
                <div
                    class="input-range-marks__mark"
                    *ngIf="maxVal"
                    (click)="handleMarkClick({ value: maxVal, _rangeValue: maxVal })"
                    [ngStyle]="{ right: '0%', textAlign: 'right' }"
                >
                    <ng-container *ngIf="maxValLabel">
                        {{ maxValLabel }}
                    </ng-container>
                    <ng-container *ngIf="!maxValLabel">
                        {{ maxVal }}
                    </ng-container>
                </div>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputRangeComponent extends FieldBaseComponent implements OnChanges {
    @Input() cssClasses: string;
    @Input() isDisabled?: boolean = false;
    @Input() label?: string;
    @Input() labelUnit?: string | number | null;
    @Input() tickmarks?: ITickmark[];
    @Input() minVal?: number;
    @Input() minValLabel?: string | null;
    @Input() maxVal?: number;
    @Input() maxValLabel?: string | null;
    @Input() controlledTrackStart?: string;
    @Input() controlledTrackEnd?: string;
    @Input() inputStepSize?: number;
    @Input() hideRangeMarks?: boolean;
    @Output() handleValueChange = new EventEmitter<ITickmark>();
    @Output() liveOnChangeValueUseWithCautionTriggersOnEverySingleValueChange = new EventEmitter<number>();

    get marks() {
        if (this.tickmarks && this.tickmarks.length)
        return this.tickmarks?.map((tickmark, index, arr) => ({
            ...tickmark,
            _rangeValue: Math.floor((index / (arr.length - 1)) * 100),
        }));
    }

    constructor(private cdr: ChangeDetectorRef) {
        super();
    }

    ngOnChanges(changes: SimpleChanges) {
        // Setting inital / default value
        // If there is a default, else take first tickmark.
        if (changes.tickmarks && !this.isDisabled) {
            this.updateInput(this.marks?.find((m) => m.isDefault) || this.marks?.shift());
        }

        this.isDisabled = changes?.isDisabled?.currentValue ?? false;
        this.cdr.markForCheck();
    }

    calcNearestTickmark(val: number) {
        return this.marks?.reduce((prev, curr) => (Math.abs(curr._rangeValue - val) < Math.abs(prev._rangeValue - val) ? curr : prev));
    }

    handleChange({ target: { value } }: any) {
        if (!this.tickmarks && !value) {
            return;
        }
        if (!this.tickmarks && value) {
            this.updateInput({ value, _rangeValue: value });
            return;
        }

        const nearestMark = this.calcNearestTickmark(value);
        if (value !== nearestMark?._rangeValue) {
            this.updateInput(nearestMark);
        }
    }

    updateInput(mark?: ITickmark) {
        if (mark) {
            this.inputControl?.setValue(mark?._rangeValue);
            this.handleValueChange?.emit(mark);
        }
    }

    handleMarkClick(mark?: ITickmark) {
        if (mark) {
            this.updateInput(mark);
            this.liveOnChangeValueUseWithCautionTriggersOnEverySingleValueChange?.emit(mark._rangeValue);
        }
    }

    handleLiveUpdates({ target: { value } }: any) {
        this.liveOnChangeValueUseWithCautionTriggersOnEverySingleValueChange?.emit(value);
    }
}
